import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useComms } from '../context/CommsContext';
import { createAuthenticatedMediaObjectUrl } from '../matrix/media';

export interface ConversationImageSourceInput {
  eventId: string;
  mxc: string;
}

export interface ConversationImageSource {
  url: string | null;
  loading: boolean;
  failed: boolean;
  retry(): void;
}

interface SourceEntry {
  mxc: string;
  attempt: number;
  active: boolean;
  url: string | null;
  loading: boolean;
  failed: boolean;
  revoke: (() => void) | null;
}

const selectedImages = (
  images: readonly ConversationImageSourceInput[],
  currentIndex: number | null,
) => {
  const selected = new Map<string, ConversationImageSourceInput>();
  if (
    currentIndex === null ||
    currentIndex < 0 ||
    currentIndex >= images.length
  )
    return selected;
  for (const index of [currentIndex - 1, currentIndex, currentIndex + 1]) {
    const image = images[index];
    if (image) selected.set(image.eventId, image);
  }
  return selected;
};

export const useConversationImageSources = (
  images: readonly ConversationImageSourceInput[],
  currentIndex: number | null,
) => {
  const { fetchMediaBlob } = useComms();
  const entriesRef = useRef(new Map<string, SourceEntry>());
  const [attempts, setAttempts] = useState(new Map<string, number>());
  const [, refresh] = useReducer((value: number) => value + 1, 0);
  const dispose = useCallback((entry: SourceEntry) => {
    entry.active = false;
    entry.revoke?.();
    entry.revoke = null;
    entry.url = null;
  }, []);

  useEffect(() => {
    const selected = selectedImages(images, currentIndex);
    for (const [eventId, entry] of entriesRef.current) {
      const image = selected.get(eventId);
      const attempt = attempts.get(eventId) ?? 0;
      if (!image || image.mxc !== entry.mxc || attempt !== entry.attempt) {
        dispose(entry);
        entriesRef.current.delete(eventId);
      }
    }

    for (const [eventId, image] of selected) {
      if (entriesRef.current.has(eventId)) continue;
      const entry: SourceEntry = {
        mxc: image.mxc,
        attempt: attempts.get(eventId) ?? 0,
        active: true,
        url: null,
        loading: true,
        failed: false,
        revoke: null,
      };
      entriesRef.current.set(eventId, entry);
      void createAuthenticatedMediaObjectUrl(fetchMediaBlob, image.mxc)
        .then((media) => {
          if (!entry.active || entriesRef.current.get(eventId) !== entry) {
            media.revoke();
            return;
          }
          entry.url = media.url;
          entry.revoke = media.revoke;
          entry.loading = false;
          refresh();
        })
        .catch(() => {
          if (!entry.active || entriesRef.current.get(eventId) !== entry)
            return;
          entry.loading = false;
          entry.failed = true;
          refresh();
        });
    }
    refresh();
  }, [attempts, currentIndex, dispose, fetchMediaBlob, images]);

  useEffect(
    () => () => {
      entriesRef.current.forEach(dispose);
      entriesRef.current.clear();
    },
    [dispose],
  );

  const retry = useCallback((eventId: string) => {
    setAttempts((current) => {
      const next = new Map(current);
      next.set(eventId, (current.get(eventId) ?? 0) + 1);
      return next;
    });
  }, []);

  const sources = new Map<string, ConversationImageSource>();
  for (const [eventId] of selectedImages(images, currentIndex)) {
    const entry = entriesRef.current.get(eventId);
    sources.set(eventId, {
      url: entry?.url ?? null,
      loading: entry?.loading ?? true,
      failed: entry?.failed ?? false,
      retry: () => retry(eventId),
    });
  }
  return sources;
};
